import { Component, computed, input } from '@angular/core';

import { InfoscreenNewsItem } from '../infoscreen-module.types';

/** Seconds per character of headline: the line moves at an even, readable pace whatever its length. */
const SECONDS_PER_CHAR = 0.16;

/**
 * The news as a running line: the station's name, then the headlines one after another, moving
 * left in an endless loop (drawn twice, moved by exactly one copy's width, so the seam never shows).
 */
@Component({
  selector: 'app-news-ticker',
  template: `
    <div class="source">{{ source() }}</div>
    <div class="viewport">
      <div class="track" [style.--duration.s]="duration()">
        @for (copy of [0, 1]; track copy) {
          <div class="row" [attr.aria-hidden]="copy === 1 ? 'true' : null">
            @for (item of items(); track $index) {
              <span class="item">
                @if (mixed() && item.source) {
                  <span class="from">{{ item.source }}</span>
                }
                {{ item.title }}
              </span>
            }
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    :host {
      flex-shrink: 0;
      display: flex;
      align-items: stretch;
      height: calc(var(--screen-h, 100vh) * 0.05);
      font-size: calc(var(--screen-h, 100vh) * 0.024);
      background: rgba(0, 0, 0, 0.28);
      color: rgb(var(--ink-rgb, 255, 255, 255));
      overflow: hidden;
    }
    .source {
      display: flex;
      align-items: center;
      padding: 0 1em;
      font-weight: 800;
      background: var(--theme-accent, #32bad4);
      color: #ffffff;
      white-space: nowrap;
      text-shadow: none;
    }
    .viewport {
      flex: 1;
      overflow: hidden;
      display: flex;
      align-items: center;
    }
    .track {
      display: flex;
      width: max-content;
      animation: news-scroll var(--duration, 60s) linear infinite;
    }
    .row {
      display: flex;
      white-space: nowrap;
    }
    .from {
      font-weight: 800;
      color: var(--theme-accent, #32bad4);
      margin-right: 0.5em;
    }
    .item {
      padding: 0 1.2em;
      &::after {
        content: '•';
        margin-left: 2.4em;
        opacity: 0.6;
      }
    }
    @keyframes news-scroll {
      from {
        transform: translateX(0);
      }
      to {
        transform: translateX(-50%);
      }
    }
  `,
})
export class NewsTickerComponent {
  readonly items = input.required<InfoscreenNewsItem[]>();
  readonly source = input<string | null>(null);

  /** More than one source: each headline says where it is from. */
  readonly mixed = computed(() => new Set(this.items().map((item) => item.source)).size > 1);

  readonly duration = computed(() =>
    Math.max(30, Math.round(this.items().reduce((n, item) => n + item.title.length + 6, 0) * SECONDS_PER_CHAR)),
  );
}
